import { useState, useEffect, useRef } from 'react';
import { GammaLensLogo } from './LandingPage';
import { Star, Lock, Calendar, Flame } from 'lucide-react';

const MktIcon = ({ size = 16, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    <rect x="2" y="7" width="4" height="9" rx="1" fill="#22c55e" opacity="0.9"/>
    <line x1="4" y1="4" x2="4" y2="7" stroke="#22c55e" strokeWidth="1.5" opacity="0.6"/>
    <line x1="4" y1="16" x2="4" y2="20" stroke="#22c55e" strokeWidth="1.5" opacity="0.6"/>
    <rect x="10" y="5" width="4" height="11" rx="1" fill="#ef4444" opacity="0.9"/>
    <line x1="12" y1="2" x2="12" y2="5" stroke="#ef4444" strokeWidth="1.5" opacity="0.6"/>
    <line x1="12" y1="16" x2="12" y2="21" stroke="#ef4444" strokeWidth="1.5" opacity="0.6"/>
    <rect x="18" y="8" width="4" height="7" rx="1" fill="#22c55e" opacity="0.7"/>
    <line x1="20" y1="5" x2="20" y2="8" stroke="#22c55e" strokeWidth="1.5" opacity="0.5"/>
    <line x1="20" y1="15" x2="20" y2="19" stroke="#22c55e" strokeWidth="1.5" opacity="0.5"/>
  </svg>
);

const LandscapeIcon = ({ size = 14, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    <rect x="2" y="3" width="9" height="8" rx="1.5" fill="currentColor" opacity="0.6"/>
    <rect x="13" y="3" width="9" height="5" rx="1.5" fill="currentColor" opacity="0.35"/>
    <rect x="2" y="13" width="5" height="8" rx="1.5" fill="currentColor" opacity="0.45"/>
    <rect x="9" y="13" width="7" height="8" rx="1.5" fill="currentColor" opacity="0.25"/>
    <rect x="18" y="10" width="4" height="11" rx="1.5" fill="currentColor" opacity="0.5"/>
  </svg>
);

const ConcentrationIcon = ({ size = 14, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    <path d="M4 20h16" stroke="currentColor" strokeWidth="1.5" opacity="0.3" strokeLinecap="round"/>
    <rect x="5" y="14" width="3" height="6" rx="1" fill="currentColor" opacity="0.4"/>
    <rect x="10" y="8" width="3" height="12" rx="1" fill="currentColor" opacity="0.6"/>
    <rect x="15" y="4" width="3" height="16" rx="1" fill="currentColor" opacity="0.9"/>
  </svg>
);

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://gammalens-api.onrender.com'
  : `http://${window.location.hostname}:8001`;

function fmtGex(n) {
  if (n == null) return '0';
  const sign = n >= 0 ? '+' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}${(n / 1e9).toFixed(1)}B`;
  if (abs >= 100e6) return `${sign}${(n / 1e6).toFixed(0)}M`;
  if (abs >= 1e6) return `${sign}${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(n / 1e3).toFixed(0)}K`;
  return `${sign}${n.toFixed(0)}`;
}

// Note: getRegimeDisplay() was removed — the headline/subheadline label is now
// produced server-side by sector_signal.sector_verdict and shipped on
// verdict.headline / verdict.subheadline. Single source of truth = backend.
// Re-deriving labels locally caused the brief and the hero to drift apart.

function getExcText(excs) {
  if (!excs || excs.length === 0) return null;
  const names = excs.slice(0, 3).join(', ');
  const more = excs.length > 3 ? ` +${excs.length - 3} more` : '';
  return `${names}${more} diverge`;
}

function fmtEarningsDate(ds) {
  if (!ds) return '';
  const d = new Date(ds + 'T12:00:00');
  if (isNaN(d)) return ds;
  return `${d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()} ${d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
}

function fmtEarningsTime(t) {
  if (!t) return 'TIME TBD';
  if (t === 'Pre-Market' || t === 'BMO') return 'PRE-MKT';
  if (t === 'After Close' || t === 'AMC') return 'AFTER CLOSE';
  return 'TIME TBD';
}

function getEarningsContext(earning) {
  if (earning.regime === 'NEGATIVE') return 'Negative gamma into earnings - amplified reaction expected';
  if (earning.regime === 'POSITIVE') return 'Positive gamma may contain reaction. Dealers suppress the move.';
  return 'Approaching earnings. Watch for gamma regime shift.';
}

const SC = {
  MOMENTUM: { accent: '#ef4444', bg: 'rgba(239,68,68,0.06)', color: '#fb7185', icon: '\u2191', urgency: 'high' },
  FLIP_WATCH: { accent: '#a78bfa', bg: 'rgba(167,139,250,0.06)', color: '#a78bfa', icon: '\u21C5', urgency: 'medium' },
  RANGE_BOUND: { accent: '#22c55e', bg: 'rgba(34,197,94,0.06)', color: '#22c55e', icon: '\u2194', urgency: 'low' },
  BREAKDOWN: { accent: '#ef4444', bg: 'rgba(239,68,68,0.06)', color: '#fb7185', icon: '\u2193', urgency: 'high' },
};

const CARD = {
  background: 'linear-gradient(145deg, #161b22 0%, #131920 100%)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 14,
};

function InfoTip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', cursor: 'help', marginLeft: 6, flexShrink: 0 }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}
      onClick={() => setShow(!show)}>
      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', fontWeight: 500, transition: 'all 0.2s' }}>?</span>
      {show && (
        <span style={{ position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', background: 'rgba(22,27,34,0.98)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 14px', fontSize: 11, color: 'rgba(255,255,255,0.7)', whiteSpace: 'normal', width: 240, lineHeight: 1.5, zIndex: 50, fontFamily: 'Inter, sans-serif', fontWeight: 400, boxShadow: '0 12px 40px rgba(0,0,0,0.5)', pointerEvents: 'none', backdropFilter: 'blur(20px)' }}>{text}</span>
      )}
    </span>
  );
}

function Tag({ color, children, subtle }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: subtle ? 9 : 10, fontWeight: 600, color, background: `${color}1a`, border: `1px solid ${color}30`, borderRadius: 20, padding: subtle ? '3px 10px' : '4px 12px', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{children}</span>;
}

function SH({ icon: I, iconColor, title, tip, action, onAction }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {I && (
          <div style={{ width: 30, height: 30, borderRadius: 9, background: `linear-gradient(135deg, ${iconColor || 'rgba(255,255,255,0.06)'}18, ${iconColor || 'rgba(255,255,255,0.06)'}08)`, border: `1px solid ${iconColor || 'rgba(255,255,255,0.1)'}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <I size={14} style={{ color: iconColor || '#a1a1aa' }} />
          </div>
        )}
        <span style={{ fontSize: 14, fontWeight: 700, color: '#f0f0f5', letterSpacing: '-0.3px', fontFamily: 'Inter, sans-serif' }}>{title}</span>
        {tip && <InfoTip text={tip} />}
      </div>
      {action && <button onClick={onAction} style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 500, transition: 'color 0.2s', letterSpacing: '-0.01em' }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
      >{action} {'\u203A'}</button>}
    </div>
  );
}

// Horizontal sector slider — replaces the previous auto-fit grid so users can
// swipe/scroll through sectors one card at a time. Snap-scrolling + clickable
// name pills (highlighted for the currently-visible card). Prev/next arrows
// appear on desktop widths only where cursor-based nav makes sense.
function SectorSlider({ items, bySector, sectorAgg, SECTOR_DISPLAY, NAV_SECTOR_KEY, loggedIn, onSignIn, onDashboard, nav, fmtSpot, CARD, isDesktop }) {
  const scrollRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateNav = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    // Active = first card whose left edge sits at (or just past) the container's left edge.
    const kids = el.children;
    const parentLeft = el.getBoundingClientRect().left;
    let idx = 0;
    for (let i = 0; i < kids.length; i++) {
      const dx = kids[i].getBoundingClientRect().left - parentLeft;
      if (dx >= -12) { idx = i; break; }
      idx = i;
    }
    setActiveIdx(idx);
  };

  useEffect(() => {
    updateNav();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
    return () => {
      el.removeEventListener('scroll', updateNav);
      window.removeEventListener('resize', updateNav);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const scrollByDir = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const first = el.children[0];
    const step = first ? first.getBoundingClientRect().width + 12 : el.clientWidth * 0.9;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const scrollToIdx = (idx) => {
    const el = scrollRef.current;
    if (!el) return;
    const target = el.children[idx];
    if (!target) return;
    // Manual left-edge scroll instead of scrollIntoView so the page doesn't
    // jump vertically on tap.
    const parentLeft = el.getBoundingClientRect().left;
    const targetLeft = target.getBoundingClientRect().left;
    el.scrollBy({ left: targetLeft - parentLeft, behavior: 'smooth' });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Sector name pills — click to jump. Active pill highlights green. */}
      <div className="hf-scroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 10, marginBottom: 10 }}>
        {items.map((s, i) => {
          const active = i === activeIdx;
          return (
            <button key={s} onClick={() => scrollToIdx(i)} style={{
              flexShrink: 0,
              padding: '5px 12px',
              borderRadius: 14,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${active ? 'rgba(34,197,94,0.28)' : 'rgba(255,255,255,0.06)'}`,
              color: active ? '#22c55e' : 'rgba(255,255,255,0.55)',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '-0.01em',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}>{SECTOR_DISPLAY[s] || s}</button>
          );
        })}
      </div>

      {/* Slider track */}
      <div ref={scrollRef} className="hf-scroll" style={{
        display: 'flex',
        gap: 12,
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        paddingBottom: 4,
        WebkitOverflowScrolling: 'touch',
      }}>
        {items.map(secKey => {
          const tickers = bySector[secKey] || [];
          if (tickers.length === 0) return null;
          const displayName = SECTOR_DISPLAY[secKey] || secKey;
          const agg = sectorAgg[secKey];
          const posCount = agg ? agg.positive : tickers.filter(t => t.regime === 'POSITIVE').length;
          const totalCount = agg ? agg.total : tickers.length;
          const posPct = totalCount > 0 ? Math.round(posCount / totalCount * 100) : 0;
          const majorityPos = totalCount > 0 && posCount / totalCount >= 0.5;

          const openSector = () => {
            if (loggedIn) {
              sessionStorage.setItem('gl_sector', NAV_SECTOR_KEY[secKey] || secKey);
              onDashboard?.('__HEATMAP_SECTOR__');
            } else onSignIn?.();
          };

          return (
            <div key={secKey} style={{
              ...CARD,
              padding: '14px 14px 12px',
              flex: '0 0 min(380px, calc(100% - 8px))',
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
            }}>
              <div onClick={openSector} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontFamily: 'Inter, sans-serif', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                <div style={{ width: 44, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', display: 'flex', flexShrink: 0 }}>
                  <div style={{ width: `${posPct}%`, background: 'rgba(34,197,94,0.55)' }} />
                  <div style={{ flex: 1, background: 'rgba(239,68,68,0.3)' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: majorityPos ? '#22c55e' : '#ef4444', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 36, textAlign: 'right' }}>{posCount}/{totalCount}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: 4 }}>
                {tickers.map(t => {
                  const isP = t.regime === 'POSITIVE';
                  const bg = isP ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)';
                  const border = isP ? 'rgba(34,197,94,0.22)' : 'rgba(239,68,68,0.22)';
                  const sym = isP ? '#4ade80' : '#f87171';
                  return (
                    <div key={t.ticker} onClick={() => nav(t.ticker)}
                      className="hf-cell-hover"
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '6px 4px', minHeight: 44, borderRadius: 6,
                        background: bg, border: `1px solid ${border}`,
                        cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace",
                        overflow: 'hidden',
                      }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: sym, letterSpacing: '-0.02em', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.ticker}</span>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', marginTop: 2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtSpot(t.spot)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Prev / next arrows — desktop only. Overlaid on the slider edges. */}
      {isDesktop && items.length > 1 && (
        <>
          <button onClick={() => scrollByDir(-1)} disabled={!canPrev} aria-label="Previous sector" style={arrowBtnStyle('left', canPrev)}>{'‹'}</button>
          <button onClick={() => scrollByDir(1)} disabled={!canNext} aria-label="Next sector" style={arrowBtnStyle('right', canNext)}>{'›'}</button>
        </>
      )}
    </div>
  );
}

function arrowBtnStyle(side, enabled) {
  return {
    position: 'absolute',
    top: '50%',
    [side]: -6,
    transform: 'translateY(-8px)',
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: enabled ? 'rgba(22,27,34,0.92)' : 'rgba(22,27,34,0.4)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: enabled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.25)',
    fontSize: 22,
    fontWeight: 400,
    lineHeight: 1,
    cursor: enabled ? 'pointer' : 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
    zIndex: 3,
    padding: 0,
    fontFamily: 'Inter, sans-serif',
    transition: 'opacity 0.2s, background 0.2s',
    opacity: enabled ? 1 : 0.5,
  };
}

export default function HomeFeed({ onSignIn, onSignUp, onDashboard, onLearn, onLogout, loggedIn, user }) {
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [earningsExpanded, setEarningsExpanded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 769px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const fetchFeed = () => {
    setLoading(true); setError(null);
    const token = localStorage.getItem('gl_token');
    const url = `${API_BASE}/api/home-feed${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    fetch(url, { signal: AbortSignal.timeout(12000) })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(d => { setFeed(d); setLoading(false); })
      .catch(e => { setError(e.message || 'Network error'); setLoading(false); });
  };

  useEffect(() => { fetchFeed(); }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const nav = (ticker) => { if (loggedIn) onDashboard?.(ticker); else onSignIn?.(); };

  if (loading) return (
    <div style={PG}>
      <style>{CSS}</style>
      <Nav loggedIn={loggedIn} user={user} onSignIn={onSignIn} onDashboard={onDashboard} onLearn={onLearn} onLogout={onLogout} isDesktop={isDesktop} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 24, height: 24, margin: '0 auto 18px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.06)', borderTopColor: 'rgba(255,255,255,0.4)', animation: 'hf-spin 0.7s linear infinite' }} />
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', fontFamily: "'Inter', sans-serif", fontWeight: 500, letterSpacing: '0.02em' }}>Loading market structure</div>
        </div>
      </div>
    </div>
  );

  if (error || !feed || feed.__fatal) return (
    <div style={PG}>
      <style>{CSS}</style>
      <Nav loggedIn={loggedIn} user={user} onSignIn={onSignIn} onDashboard={onDashboard} onLearn={onLearn} onLogout={onLogout} isDesktop={isDesktop} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 380, padding: '0 24px' }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginBottom: 8, fontFamily: 'Inter, sans-serif', letterSpacing: '-0.3px' }}>Data temporarily unavailable</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, marginBottom: 24 }}>Levels refresh at 9:45 AM ET</div>
          <button onClick={fetchFeed} className="hf-btn-primary">Retry</button>
        </div>
      </div>
    </div>
  );

  const { verdict, setups = [], watchlist = [], landscape = [], sectors = [], concentration = { calls: [], puts: [] }, gamma_movers = [], earnings = [] } = feed;
  const regimeIsPos = verdict?.regime === 'POSITIVE';
  // Headline/subheadline come straight from the API verdict (sector-derived
  // server-side via sector_signal.sector_verdict). No local re-derivation.
  const hl = {
    headline: verdict?.headline || 'Loading...',
    sub: verdict?.subheadline || '',
  };
  const time = verdict ? new Date(verdict.updated_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) : '';
  const flipCount = verdict?.near_flip_count ?? setups.filter(s => s.type === 'FLIP_WATCH').length;
  const flippedCount = gamma_movers.length;

  const uniqueSetups = setups.filter((s, i, arr) => arr.findIndex(x => x.ticker === s.ticker) === i);
  const visibleSetups = loggedIn ? uniqueSetups : uniqueSetups.slice(0, Math.min(3, uniqueSetups.length));
  const hiddenSetups = loggedIn ? 0 : Math.max(0, uniqueSetups.length - 3);
  const maxVisibleEarnings = loggedIn ? (earningsExpanded ? earnings.length : 3) : 2;
  const visibleEarnings = earnings.slice(0, maxVisibleEarnings);
  const hiddenEarnings = earnings.length - maxVisibleEarnings;
  const earningsGated = !loggedIn && earnings.length > 2;

  const maxCallGex = Math.max(...(concentration.calls.map(c => Math.abs(c.gex))), 1);
  const maxPutGex = Math.max(...(concentration.puts.map(c => Math.abs(c.gex))), 1);

  // ── Sector-grouped landscape (replaces old flat landscape + sector breakdown) ──
  // Fixed sector order per spec. Backend uses "Semiconductors"; the frontend
  // abbreviation map ("Semi") kicks in only at render time. Any sector that
  // shows up in data but isn't in this list is appended at the end.
  const SECTOR_ORDER = [
    'ETF', 'Tech', 'Semiconductors', 'Finance', 'Healthcare', 'Consumer',
    'Energy', 'Industrial', 'Crypto', 'Meme', 'Media', 'Other',
  ];
  const SECTOR_DISPLAY = { Semiconductors: 'Semi', Cryptocurrency: 'Crypto', 'Consumer Discretionary': 'Consumer' };
  const NAV_SECTOR_KEY = { ...SECTOR_DISPLAY, Financials: 'Finance', Industrials: 'Industrial' };

  // Format spot compactly so a wide tile doesn't blow out mobile layout.
  //   ≥ 1000 → whole dollars: $1,234
  //   ≥ 100  → whole dollars: $438
  //   < 100  → 2 decimals:    $8.20
  const fmtSpot = (v) => {
    if (v == null || isNaN(v)) return '—';
    if (v >= 1000) return `$${Math.round(v).toLocaleString()}`;
    if (v >= 100) return `$${Math.round(v)}`;
    return `$${v.toFixed(2)}`;
  };

  // Group landscape items by sector; within each sector sort by |net_gex| desc
  // so the biggest names lead each card.
  const bySector = {};
  for (const t of landscape) {
    const s = t.sector || 'Other';
    if (!bySector[s]) bySector[s] = [];
    bySector[s].push(t);
  }
  for (const s of Object.keys(bySector)) {
    bySector[s].sort((a, b) => Math.abs(b.net_gex) - Math.abs(a.net_gex));
  }
  // Merge fixed order + any leftover sectors present in data, alphabetized at end.
  const knownOrdered = SECTOR_ORDER.filter(s => bySector[s]);
  const extras = Object.keys(bySector).filter(s => !SECTOR_ORDER.includes(s)).sort();
  const orderedSectors = [...knownOrdered, ...extras];

  // Sector aggregates (X/Y ratio + bar) — matches shape returned by /api/home-feed.
  const sectorAgg = {};
  for (const s of sectors) sectorAgg[s.sector] = s;

  // \u2500\u2500 Per-sector-ETF regime lookup (powers the character line + fact chips).
  //   Landscape items carry {ticker, regime} for every ticker including the
  //   11 sector ETFs, so no new fetch \u2014 we just index by symbol.
  const sectorEtfRegime = {};
  for (const t of landscape) {
    if (t.ticker && t.regime) sectorEtfRegime[t.ticker] = t.regime;
  }

  // Growth / defensive / cyclical buckets for the character line.
  const SECTOR_BUCKETS = {
    growth:    { etfs: ['XLK', 'SMH', 'XLY', 'XLF', 'XLC'], noun: 'growth sectors' },
    defensive: { etfs: ['XLP', 'XLU', 'XLV', 'XLRE'],       noun: 'defensives' },
    cyclical:  { etfs: ['XLE', 'XLI', 'XLB'],               noun: 'cyclicals' },
  };
  const SECTOR_ETF_NAME = {
    XLK: 'Tech', XLF: 'Financials', XLE: 'Energy', XLV: 'Healthcare',
    XLI: 'Industrials', XLY: 'Consumer Discretionary', XLP: 'Staples',
    XLU: 'Utilities', XLRE: 'Real Estate', XLB: 'Materials', XLC: 'Communication',
    SMH: 'Semis',
  };

  const positiveByBucket = Object.fromEntries(
    Object.entries(SECTOR_BUCKETS).map(([k, { etfs }]) => [
      k,
      etfs.filter(sym => sectorEtfRegime[sym] === 'POSITIVE'),
    ])
  );

  // Character line: rule-based, MUST honestly fall back to "broad-based" when
  // no bucket dominates. Never fabricate a tilt. Branches on regime so a
  // negative-gamma day describes where AMPLIFICATION concentrates rather
  // than talking about the shrinking positive minority (which reads as if
  // it contradicts the negative-gamma headline).
  const buildCharacterLine = () => {
    const pos = (verdict && verdict.sector_positive_count) || 0;
    const neg = (verdict && verdict.sector_negative_count) || 0;
    const total = (verdict && verdict.sector_total) || 0;
    if (total === 0) return null;

    const headline = (verdict && verdict.headline) || '';
    const isNeg = /negative gamma/i.test(headline);

    // Shared bucket-share calc \u2014 parametrized by which regime we're profiling.
    const rankBuckets = (byBucket, denom) => {
      const share = {};
      for (const [k, { etfs }] of Object.entries(SECTOR_BUCKETS)) {
        const bucketTotal = etfs.filter(s => sectorEtfRegime[s]).length;
        const inBucket = byBucket[k].length;
        share[k] = {
          bucketTotal,
          internalRatio: bucketTotal > 0 ? inBucket / bucketTotal : 0,
          shareOfAll: denom > 0 ? inBucket / denom : 0,
        };
      }
      return Object.entries(share).sort(
        (a, b) => b[1].shareOfAll - a[1].shareOfAll
      );
    };

    // \u2500\u2500 NEGATIVE-gamma regime \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    if (isNeg) {
      if (neg === 0) return null;
      const negativeByBucket = Object.fromEntries(
        Object.entries(SECTOR_BUCKETS).map(([k, { etfs }]) => [
          k,
          etfs.filter(sym => sectorEtfRegime[sym] === 'NEGATIVE'),
        ])
      );
      const ranked = rankBuckets(negativeByBucket, neg);
      const [leaderKey, leaderInfo] = ranked[0];

      if (leaderInfo.shareOfAll <= 0.5 || leaderInfo.internalRatio < 0.6) {
        return 'Amplification is broad-based across sectors.';
      }
      const leaderNames = negativeByBucket[leaderKey]
        .map(sym => SECTOR_ETF_NAME[sym])
        .filter(Boolean)
        .slice(0, 3);
      const namesFragment = leaderNames.length ? ` \u2014 ${leaderNames.join(', ')} lead negative` : '';

      if (leaderKey === 'growth') {
        return `Amplification is concentrated in growth sectors${namesFragment}.`;
      }
      if (leaderKey === 'defensive') {
        return `Amplification is concentrated in defensives${namesFragment} \u2014 a broader risk-off character.`;
      }
      return `Amplification is concentrated in cyclicals${namesFragment}.`;
    }

    // \u2500\u2500 POSITIVE / MIXED regime \u2014 existing positive-tilt story \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    if (pos === 0) return null;
    const ranked = rankBuckets(positiveByBucket, pos);
    const [leaderKey, leaderInfo] = ranked[0];

    if (leaderInfo.shareOfAll <= 0.5 || leaderInfo.internalRatio < 0.6) {
      return 'Positive gamma is broad-based, with no clear sector tilt.';
    }

    const leaderNames = positiveByBucket[leaderKey]
      .map(sym => SECTOR_ETF_NAME[sym])
      .filter(Boolean)
      .slice(0, 3);
    const namesFragment = leaderNames.length ? ` \u2014 ${leaderNames.join(', ')} lead positive` : '';

    if (leaderKey === 'growth') {
      return `Suppression is concentrated in growth sectors${namesFragment}${leaderNames.length ? ', while defensives lean negative.' : '.'}`;
    }
    if (leaderKey === 'defensive') {
      return `Positive gamma is led by defensives${namesFragment} \u2014 a more risk-off character.`;
    }
    return `Positive gamma is led by cyclicals${namesFragment}.`;
  };
  const characterLine = buildCharacterLine();

  // Strongest / weakest sector by positive-ratio for the fact chips.
  const sectorsForRank = sectors.filter(s => s.total > 0);
  const sortedBySectorRatio = [...sectorsForRank].sort(
    (a, b) => (b.positive / b.total) - (a.positive / a.total)
  );
  const strongestSector = sortedBySectorRatio[0];
  const weakestSector = sortedBySectorRatio[sortedBySectorRatio.length - 1];
  const divergingCount = (verdict && verdict.exceptions) ? verdict.exceptions.length : 0;

  // Split the sector-derived headline so we can color-code the regime words.
  // sector_signal.py hard-codes these three sentence forms, so pattern-match is stable.
  const headlineText = (verdict && verdict.headline) || '';
  const isPosHeadline = /positive gamma/i.test(headlineText);
  const isNegHeadline = /negative gamma/i.test(headlineText);
  const isMixedHeadline = /mixed gamma/i.test(headlineText);
  const regimeAccent = isPosHeadline ? '#22c55e' : isNegHeadline ? '#ef4444' : isMixedHeadline ? '#f59e0b' : '#94a3b8';
  const regimeWord = isPosHeadline ? 'positive gamma'
                   : isNegHeadline ? 'negative gamma'
                   : isMixedHeadline ? 'mixed gamma'
                   : null;

  const formatStat = (v) => v > 0 ? String(v) : '\u2014';

  // Note: the "Suppression / Momentum / Mixed regime" pill from the previous
  // hero was removed with the editorial redesign; the regime is now encoded
  // in the statement headline itself. `getRegimeTag`/`regimeTag` deleted.

  return (
    <div style={PG}>
      <style>{CSS}</style>
      <Nav loggedIn={loggedIn} user={user} onSignIn={onSignIn} onDashboard={onDashboard} onLearn={onLearn} onLogout={onLogout} isDesktop={isDesktop} />

      {/* Welcome banner — non-logged only */}
      {!loggedIn && (
        <div style={{ background: 'linear-gradient(180deg, rgba(34,197,94,0.025), transparent)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.18)', borderRadius: 4, fontSize: 9, fontWeight: 700, color: '#22c55e', letterSpacing: '0.14em', fontFamily: "'JetBrains Mono', monospace" }}>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#22c55e' }} />
                FREE BETA
              </span>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontFamily: 'Inter, sans-serif', fontWeight: 500, letterSpacing: '-0.1px' }}>
                100+ tickers tracked in real-time
              </span>
            </div>
            <button onClick={() => (onSignUp || onSignIn)?.()} style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.95)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            >Sign up free {'\u2192'}</button>
          </div>
        </div>
      )}

      {/* Index strip */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="hf-idx-strip" style={{ maxWidth: 1200, margin: '0 auto', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflowX: 'auto', whiteSpace: 'nowrap' }}>
          {['SPY', 'QQQ', 'IWM', 'DIA'].map((sym, i, arr) => {
            const d = (feed?.indices || []).find(x => x.ticker === sym);
            const spot = d?.spot;
            const pct = d?.change_pct || 0;
            const regime = d?.regime;
            const isPos = pct >= 0;
            return (
              <div key={sym} onClick={() => nav(sym)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', transition: 'opacity 0.15s', flexShrink: 0, paddingRight: i < arr.length - 1 ? 24 : 0, borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: regime === 'POSITIVE' ? '#22c55e' : regime === 'NEGATIVE' ? '#ef4444' : '#636366', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.85)', fontFamily: "'JetBrains Mono', monospace" }}>{sym}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace" }}>{spot ? `$${spot.toFixed(2)}` : '--'}</span>
                {pct !== 0 && <span style={{ fontSize: 11, fontWeight: 600, color: isPos ? '#22c55e' : '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>{isPos ? '+' : ''}{pct.toFixed(2)}%</span>}
              </div>
            );
          })}
        </div>
      </div>

      <main className="hf-main" style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px 60px' }}>

        {/* ══ MARKET BRIEF (editorial hero — replaces the old ticker-count grid) ══ */}
        {verdict && (
        <section style={{ marginBottom: 48 }}>
          <SH icon={MktIcon} iconColor="#60a5fa" title="Market brief" tip="Sector-breadth read on today's gamma structure. Observational only — not a forecast." />
          <div style={{ ...CARD, padding: 'clamp(20px, 4vw, 32px)', position: 'relative', overflow: 'hidden' }}>
            {/* Ambient glow — tinted by the sector-derived regime so hero pieces agree. */}
            <div style={{ position: 'absolute', top: -120, left: -80, width: 300, height: 300, borderRadius: '50%', filter: 'blur(100px)', opacity: 0.15, background: regimeAccent, pointerEvents: 'none' }} />
            <div style={{ position: 'relative' }}>
              {/* Market status */}
              <div style={{ position: 'absolute', top: 0, right: 0 }}>
                {feed.holiday ? (
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 10, color: '#f59e0b', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                      {feed.holiday.type === 'early_close' ? 'EARLY CLOSE' : 'HOLIDAY'}
                    </span>
                    <div className="hf-closed-sub" style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', marginTop: 2, fontFamily: 'Inter, sans-serif' }}>
                      {feed.holiday.name}{feed.holiday.type === 'early_close' && feed.holiday.early_close_time ? ` · Closes ${feed.holiday.early_close_time} ET` : ''}
                    </div>
                  </div>
                ) : feed.data_refreshed ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', animation: 'hf-pulse 2s ease infinite', flexShrink: 0 }} />
                    <span style={{ fontSize: 10, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>LIVE</span>
                    <span className="hf-live-time" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>{time} ET</span>
                  </div>
                ) : (
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 10, color: '#ef4444', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>CLOSED</span>
                    <div className="hf-closed-sub" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2, fontFamily: 'Inter, sans-serif' }}>Refreshes next trading session, 9:45 ET</div>
                  </div>
                )}
              </div>

              {/* Eyebrow — sits above the headline; the market-state badge above
                  is absolutely-positioned top-right and can wrap onto its own
                  row at narrow widths (paddingRight buffer + smaller text). */}
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.16em', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', marginBottom: 12 }}>Market brief</div>

              {/* STATEMENT HEADLINE — regime as a sentence, regime words color-coded.
                  Scales clamp(22px … 28px) so mobile 360px never clips. */}
              <h1 style={{ fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: 700, color: '#ffffff', lineHeight: 1.2, marginBottom: 10, letterSpacing: '-0.4px', fontFamily: 'Inter, sans-serif', paddingRight: 120 }}>
                {regimeWord
                  ? <>The market is in <span style={{ color: regimeAccent }}>{regimeWord}</span>.</>
                  : (verdict && verdict.headline) || 'Data refreshing.'}
              </h1>

              {/* MEANING — historical / observational tendency. Reused from
                  sector_verdict subheadline so brief + landing say the same thing. */}
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, maxWidth: 560, marginBottom: characterLine ? 16 : 24, fontFamily: 'Inter, sans-serif' }}>{(verdict && verdict.subheadline) || ''}</p>

              {/* CHARACTER LINE — new. Dynamic sentence about which BUCKET of
                  sectors is positive. Honest no-tilt fallback baked in. Only
                  renders when we have sector data — never fabricates a story. */}
              {characterLine && (
                <div style={{ borderLeft: `2px solid ${regimeAccent}`, paddingLeft: 12, marginBottom: 24, maxWidth: 560 }}>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55, margin: 0, fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>{characterLine}</p>
                </div>
              )}

              {/* Breadth bar — sector-driven so it tells the same story as the
                  sector-derived headline above (no contradiction between hero
                  pieces). The broad positive_count/negative_count remain in
                  the stat grid below where the all-tickers count is correct. */}
              {(verdict.sector_total || 0) > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.04)', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${(verdict.sector_positive_count / verdict.sector_total) * 100}%`, background: 'linear-gradient(90deg, rgba(34,197,94,0.6), rgba(34,197,94,0.35))', transition: 'width 0.8s ease' }} />
                    <div style={{ flex: 1, background: 'linear-gradient(90deg, rgba(239,68,68,0.2), rgba(239,68,68,0.35))' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>
                    <span style={{ color: 'rgba(34,197,94,0.7)' }}>{verdict.sector_positive_count} of {verdict.sector_total} sectors suppressing</span>
                    <span style={{ color: 'rgba(239,68,68,0.7)' }}>{verdict.sector_negative_count} amplifying</span>
                  </div>
                </div>
              )}

              {/* FACT CHIPS \u2014 flexible pill row that wraps naturally at any
                  width. Chips are facts only (no directives). Rendered only
                  when we have sector data \u2014 cold cache degrades gracefully. */}
              {(verdict.sector_total || 0) > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {flippedCount > 0 && (
                    <span onClick={() => {
                      if (loggedIn) {
                        sessionStorage.setItem('gl_flipped', JSON.stringify(gamma_movers.map(m => m.ticker)));
                        onDashboard?.('__HEATMAP_FLIPPED__');
                      } else onSignIn?.();
                    }} style={{ cursor: 'pointer' }}>
                      <Tag color="#f59e0b">{flippedCount} sectors flipped overnight</Tag>
                    </span>
                  )}
                  {strongestSector && strongestSector.positive === strongestSector.total && (
                    <Tag color="#22c55e">{strongestSector.sector} strongest positive</Tag>
                  )}
                  {weakestSector && weakestSector !== strongestSector && weakestSector.total > 0 && (weakestSector.positive / weakestSector.total) <= 0.2 && (
                    <Tag color="#ef4444">{weakestSector.sector} most negative</Tag>
                  )}
                  {divergingCount > 0 && (
                    <span onClick={() => {
                      if (loggedIn) {
                        sessionStorage.setItem('gl_exceptions', JSON.stringify(verdict.exceptions));
                        onDashboard?.('__HEATMAP_EXCEPTIONS__');
                      } else onSignIn?.();
                    }} style={{ cursor: 'pointer' }}>
                      <Tag color="#a78bfa">{divergingCount} diverging</Tag>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {/* ══ ACTIVE SETUPS ══ */}
        <section style={{ marginBottom: 48 }}>
          <SH icon={Flame} iconColor="#f59e0b" title="Active setups" tip="Tickers with notable gamma positioning - not trade recommendations" action={isDesktop ? "View in dashboard" : undefined} onAction={() => nav()} />
          {setups.length === 0 ? (
            <div style={{ ...CARD, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>No notable setups detected</div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }} className="hf-scroll">
              {visibleSetups.map((s, i) => {
                const sc = SC[s.type] || SC.MOMENTUM;
                const dist = s.spot && s.level ? Math.abs(s.spot - s.level) / s.spot * 100 : null;
                return (
                  <div key={i} onClick={() => nav(s.ticker)} className="hf-card hf-setup-card" style={{ minWidth: 180, maxWidth: 200, flexShrink: 0, background: '#131920', border: `1px solid ${sc.accent}20`, borderRadius: 12, padding: 0, cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    {/* Left accent strip with urgency pulse */}
                    <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: sc.accent, opacity: sc.urgency === 'high' ? 0.9 : 0.5 }} />
                    {sc.urgency === 'high' && <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: sc.accent, animation: 'hf-pulse 2s ease infinite' }} />}

                    <div style={{ padding: '14px 14px 12px 16px' }}>
                      {/* Row 1: Ticker + Type icon */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{ fontSize: 18, fontWeight: 800, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px' }}>{s.ticker}</span>
                          <span style={{ fontSize: 11, color: s.change_pct >= 0 ? '#22c55e' : '#ef4444', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{s.change_pct >= 0 ? '+' : ''}{s.change_pct.toFixed(2)}%</span>
                        </div>
                        <span style={{ fontSize: 14, color: sc.color, opacity: 0.8 }}>{sc.icon}</span>
                      </div>

                      {/* Row 2: Spot price */}
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
                        ${s.spot.toFixed(2)}
                      </div>

                      {/* Row 3: Description */}
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'Inter, sans-serif' }}>{s.description}</div>

                      {/* Row 4: Key level bar — unique to GammaLens */}
                      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 6, padding: '8px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                          <span style={{ fontSize: 8, fontWeight: 700, color: sc.color, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>{s.level_label}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace" }}>${Math.round(s.level)}</span>
                        </div>
                        {dist !== null && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${Math.min(100, Math.max(5, 100 - dist * 15))}%`, background: `${sc.accent}60`, borderRadius: 2, transition: 'width 0.5s ease' }} />
                            </div>
                            <span style={{ fontSize: 9, color: sc.color, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, flexShrink: 0 }}>{dist.toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bottom type badge */}
                    <div style={{ borderTop: `1px solid ${sc.accent}15`, padding: '6px 16px', background: `${sc.accent}06` }}>
                      <span style={{ fontSize: 8, fontWeight: 700, color: sc.color, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", opacity: 0.8 }}>{s.type.replace('_', ' ')}</span>
                    </div>
                  </div>
                );
              })}
              {hiddenSetups > 0 && (
                <div onClick={onSignIn} className="hf-card" style={{ minWidth: 160, flexShrink: 0, border: '1px dashed rgba(255,255,255,0.06)', borderRadius: 14, padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'transparent' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>+{hiddenSetups}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>more setups</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(34,197,94,0.7)' }}>Sign in {'\u2192'}</div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ══ WATCHLIST ══ */}
        <section style={{ marginBottom: 48 }}>
          <SH icon={Star} iconColor="#eab308" title="Watchlist" tip="Your tracked tickers with real-time gamma levels" action={loggedIn && watchlist.length > 0 ? 'Edit' : undefined} onAction={() => onDashboard?.('__SEARCH__')} />
          <div style={{ ...CARD, overflow: 'hidden' }}>
            {!loggedIn ? (
              <div style={{ padding: '36px 24px', textAlign: 'center' }}>
                <Lock size={16} style={{ color: 'rgba(255,255,255,0.15)', marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: 'Inter, sans-serif', letterSpacing: '-0.2px' }}>Sign in to track your watchlist</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 18 }}>Pin up to 10 tickers with live gamma levels</div>
                <button onClick={onSignIn} className="hf-btn-primary">Sign in {'\u2192'}</button>
              </div>
            ) : watchlist.length === 0 ? (
              <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>Add tickers from the dashboard to build your watchlist</div>
                <button onClick={() => onDashboard?.('__SEARCH__')} className="hf-btn-primary">Add tickers {'\u2192'}</button>
              </div>
            ) : watchlist.map((t, idx) => (
              <div key={t.ticker} onClick={() => nav(t.ticker)} style={{ padding: '14px 16px', borderBottom: idx < watchlist.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', cursor: 'pointer', transition: 'background 0.2s' }} className="hf-row-hover">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Left: Ticker + Price + Change + Regime */}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>{t.ticker}</span>
                      <span className="hf-wl-name" style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}>{t.name || ''}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>${t.spot?.toFixed(2) || '--'}</span>
                      <span style={{ fontSize: 11, color: t.change_pct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                        {t.change_pct != null ? `${t.change_pct >= 0 ? '+' : ''}${t.change_pct.toFixed(2)}%` : '--'}
                      </span>
                      <Tag color={t.regime === 'POSITIVE' ? '#22c55e' : '#ef4444'} subtle>{t.regime === 'POSITIVE' ? '+\u03B3' : '\u2013\u03B3'}</Tag>
                    </div>
                  </div>
                  {/* Right: Range bar — desktop only */}
                  {t.put_wall && t.call_wall && t.spot && (
                    <div className="hf-wl-range" style={{ width: 120, flexShrink: 0, marginLeft: 16 }}>
                      <div style={{ width: 120, height: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 2, position: 'relative' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${t.gamma_flip ? Math.max(0, Math.min(100, ((t.gamma_flip - t.put_wall) / (t.call_wall - t.put_wall) * 100))) : 50}%`, background: 'rgba(239,68,68,0.15)', borderRadius: '2px 0 0 2px' }} />
                        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${t.gamma_flip ? Math.max(0, Math.min(100, ((t.call_wall - t.gamma_flip) / (t.call_wall - t.put_wall) * 100))) : 50}%`, background: 'rgba(34,197,94,0.15)', borderRadius: '0 2px 2px 0' }} />
                        {t.gamma_flip && <div style={{ position: 'absolute', top: -1, bottom: -1, left: `${Math.max(0, Math.min(100, ((t.gamma_flip - t.put_wall) / (t.call_wall - t.put_wall) * 100)))}%`, width: 1.5, background: '#a78bfa', borderRadius: 1 }} />}
                        <div style={{ position: 'absolute', top: '50%', left: `${Math.min(100, Math.max(0, (t.spot - t.put_wall) / (t.call_wall - t.put_wall) * 100))}%`, transform: 'translate(-50%, -50%)', width: 6, height: 6, borderRadius: '50%', background: '#fff', boxShadow: '0 0 8px rgba(255,255,255,0.4)' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                        <span style={{ color: 'rgba(239,68,68,0.7)', fontWeight: 600 }}>PW ${Math.round(t.put_wall)}</span>
                        <span style={{ color: 'rgba(34,197,94,0.7)', fontWeight: 600 }}>CW ${Math.round(t.call_wall)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ══ GAMMA LANDSCAPE ══ */}
        {landscape.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <SH icon={LandscapeIcon} iconColor="#22c55e" title="Gamma landscape by sector" tip="Each sector card groups its tickers. Green tile = positive gamma (suppress), red tile = negative gamma (amplify). Tap a ticker to open its dashboard." action={isDesktop ? "View details" : undefined} onAction={() => nav()} />
          {/* Slider replaces the previous auto-fit grid \u2014 one sector card at a
              time (mobile) with snap-scrolling + clickable name pills. */}
          <SectorSlider
            items={orderedSectors.filter(s => (bySector[s] || []).length > 0)}
            bySector={bySector}
            sectorAgg={sectorAgg}
            SECTOR_DISPLAY={SECTOR_DISPLAY}
            NAV_SECTOR_KEY={NAV_SECTOR_KEY}
            loggedIn={loggedIn}
            onSignIn={onSignIn}
            onDashboard={onDashboard}
            nav={nav}
            fmtSpot={fmtSpot}
            CARD={CARD}
            isDesktop={isDesktop}
          />

          {/* Legend \u2014 kept from the old flat landscape. */}
          <div style={{ display: 'flex', gap: 16, marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 10, color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 3, background: 'rgba(34,197,94,0.35)' }} /> +{'\u03B3'} Suppress</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 3, background: 'rgba(239,68,68,0.35)' }} /> {'\u2013\u03B3'} Amplify</span>
          </div>
        </section>
        )}

        {/* Sector breakdown section removed — sector ratios + tickers are now
            combined into the "Gamma landscape by sector" card grid above. */}

        {/* ══ EARNINGS ══ */}
        {earnings.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <SH icon={Calendar} iconColor="#a78bfa" title="Earnings + gamma" tip="Upcoming earnings - negative gamma may amplify the post-report move" action={earnings.length > 3 && !earningsExpanded ? 'Show all' : earnings.length > 3 ? 'Show less' : undefined} onAction={() => setEarningsExpanded(!earningsExpanded)} />
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }} className="hf-scroll">
            {visibleEarnings.map((e, i) => (
              <div key={i} onClick={() => nav(e.ticker)} className="hf-card" style={{ minWidth: 170, maxWidth: 190, flexShrink: 0, ...CARD, padding: '14px 16px', cursor: 'pointer' }}>
                <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8, fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.7)' }}>{fmtEarningsDate(e.date)} {'\u00B7'} <span style={{ color: (e.time === 'Pre-Market' || e.time === 'BMO') ? '#f59e0b' : (e.time === 'After Close' || e.time === 'AMC') ? '#60a5fa' : 'rgba(255,255,255,0.5)' }}>{fmtEarningsTime(e.time)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.3px' }}>{e.ticker}</span>
                  <Tag color={e.regime === 'POSITIVE' ? '#22c55e' : e.regime === 'NEGATIVE' ? '#ef4444' : '#71717a'} subtle>{e.regime === 'POSITIVE' ? '+\u03B3' : e.regime === 'NEGATIVE' ? '\u2013\u03B3' : '?'}</Tag>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, fontFamily: 'Inter, sans-serif', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.context || getEarningsContext(e)}</div>
              </div>
            ))}
            {earningsGated ? (
              <div onClick={onSignIn} className="hf-card" style={{ minWidth: 140, flexShrink: 0, border: '1px dashed rgba(255,255,255,0.06)', borderRadius: 14, padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'transparent' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>+{hiddenEarnings}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>more earnings</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(34,197,94,0.7)' }}>Sign in {'\u2192'}</div>
              </div>
            ) : hiddenEarnings > 0 && loggedIn ? (
              <div onClick={() => setEarningsExpanded(!earningsExpanded)} className="hf-card" style={{ minWidth: 130, flexShrink: 0, border: '1px dashed rgba(255,255,255,0.06)', borderRadius: 14, padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'transparent' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>+{hiddenEarnings}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>{earningsExpanded ? 'show less' : 'show all'}</div>
              </div>
            ) : null}
          </div>
        </section>
        )}

        {/* ══ CONCENTRATION ══ */}
        {(concentration.calls.length > 0 || concentration.puts.length > 0) && (
        <section style={{ marginBottom: 48, position: 'relative' }}>
          <SH icon={ConcentrationIcon} iconColor="#fb7185" title="Dealer concentration" tip="Heaviest dealer walls across all tickers. Badge shows expiration stacking - more expirations = structurally stronger wall" />
          <div style={{ ...CARD, padding: '20px', position: 'relative', overflow: 'hidden' }}>
            {!loggedIn && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(transparent 0%, rgba(13,17,23,0.97) 50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 24 }}>
                <Lock size={15} style={{ color: 'rgba(255,255,255,0.15)', marginBottom: 10 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8, fontFamily: 'Inter, sans-serif', letterSpacing: '-0.2px' }}>Sign in to view full concentration</div>
                <button onClick={onSignIn} className="hf-btn-primary">Sign in {'\u2192'}</button>
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', color: '#22c55e', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Call walls</span>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Resistance</span>
              </div>
              {concentration.calls.map((c, i) => (
                <div key={c.ticker} onClick={() => nav(c.ticker)} className="hf-con-row hf-row-hover" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer', borderBottom: i < concentration.calls.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontFamily: "'JetBrains Mono', monospace", width: 48 }}>{c.ticker}</span>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontFamily: "'JetBrains Mono', monospace", width: 60 }}>${Math.round(c.level)}</span>
                  <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.max(5, (Math.abs(c.gex) / maxCallGex) * 100)}%`, background: 'rgba(34,197,94,0.5)', borderRadius: 2, transition: 'width 0.3s ease' }} />
                  </div>
                  <span style={{ fontSize: 10, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace", width: 48, textAlign: 'right', fontWeight: 600 }}>{fmtGex(c.gex)}</span>
                  {c.stacking > 0 ? (
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", background: c.stacking >= 3 ? 'rgba(34,197,94,0.1)' : c.stacking >= 2 ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.04)', color: c.stacking >= 3 ? '#22c55e' : c.stacking >= 2 ? '#f59e0b' : 'rgba(255,255,255,0.4)', border: `1px solid ${c.stacking >= 3 ? 'rgba(34,197,94,0.15)' : c.stacking >= 2 ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.06)'}`, whiteSpace: 'nowrap', width: 36, textAlign: 'center' }}>{c.stacking}x</span>
                  ) : <span style={{ width: 36 }} />}
                </div>
              ))}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', color: '#ef4444', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Put walls</span>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Support</span>
              </div>
              {concentration.puts.map((p, i) => (
                <div key={p.ticker} onClick={() => nav(p.ticker)} className="hf-con-row hf-row-hover" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer', borderBottom: i < concentration.puts.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontFamily: "'JetBrains Mono', monospace", width: 48 }}>{p.ticker}</span>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontFamily: "'JetBrains Mono', monospace", width: 60 }}>${Math.round(p.level)}</span>
                  <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.max(5, (Math.abs(p.gex) / maxPutGex) * 100)}%`, background: 'rgba(239,68,68,0.5)', borderRadius: 2, transition: 'width 0.3s ease' }} />
                  </div>
                  <span style={{ fontSize: 10, color: '#ef4444', fontFamily: "'JetBrains Mono', monospace", width: 48, textAlign: 'right', fontWeight: 600 }}>{fmtGex(p.gex)}</span>
                  {p.stacking > 0 ? (
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", background: p.stacking >= 3 ? 'rgba(34,197,94,0.1)' : p.stacking >= 2 ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.04)', color: p.stacking >= 3 ? '#22c55e' : p.stacking >= 2 ? '#f59e0b' : 'rgba(255,255,255,0.4)', border: `1px solid ${p.stacking >= 3 ? 'rgba(34,197,94,0.15)' : p.stacking >= 2 ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.06)'}`, whiteSpace: 'nowrap', width: 36, textAlign: 'center' }}>{p.stacking}x</span>
                  ) : <span style={{ width: 36 }} />}
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

      </main>

      {/* ══ FOOTER ══ */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.04)', padding: '20px 24px 28px' }}>
        <div className="hf-mob-footer" style={{ display: 'none', justifyContent: 'center', marginBottom: 14 }}>
          <button onClick={onLearn} className="hf-btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
            Learn GEX
          </button>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', fontWeight: 500 }}>For informational and educational purposes only. Not financial advice.</span>
        </div>
      </footer>
    </div>
  );
}

/* ── Sub-components ── */

function Nav({ loggedIn, user, onSignIn, onDashboard, onLearn, onLogout, isDesktop }) {
  const name = user?.display_name || user?.email?.split('@')[0] || '';
  const initials = name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '';
  return (
    <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(13,17,23,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GammaLensLogo size={24} />
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f4f4f5', letterSpacing: '-0.4px', fontFamily: 'Inter, sans-serif' }}>GammaLens</span>
          <span style={{ fontSize: 8, fontWeight: 600, color: 'rgba(34,197,94,0.7)', letterSpacing: '1.5px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.12)', borderRadius: 4, padding: '2px 6px', fontFamily: "'JetBrains Mono', monospace" }}>BETA</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {!isDesktop && (
            <button onClick={() => onDashboard?.('__SEARCH__')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', marginRight: 6, padding: 0, transition: 'all 0.2s' }}
              aria-label="Search ticker"
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>
          )}
          {isDesktop && <div style={{ display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 3, marginRight: 8 }}>
            <NP active>Home</NP>
            <NP onClick={() => onDashboard?.()}>Dashboard</NP>
            <NP onClick={onLearn}>Learn</NP>
          </div>}
          {loggedIn ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(34,197,94,0.8) 0%, rgba(34,197,94,0.5) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#000', flexShrink: 0, letterSpacing: '0.02em' }} title={name}>{initials}</div>
              <button onClick={onLogout} style={{ padding: '6px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)', fontSize: 10, fontWeight: 500, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = 'rgba(239,68,68,0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
              >Sign out</button>
            </div>
          ) : (
            <button onClick={onSignIn} style={{ padding: '7px 16px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >Sign In</button>
          )}
        </div>
      </div>
    </nav>
  );
}

function NP({ children, active, onClick }) {
  return <button onClick={onClick} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: active ? 600 : 500, background: active ? 'rgba(255,255,255,0.1)' : 'transparent', color: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.55)', border: 'none', cursor: onClick ? 'pointer' : 'default', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', letterSpacing: '-0.01em' }}>{children}</button>;
}

const PG = { minHeight: '100vh', background: '#0d1117', color: '#e4e4e7', fontFamily: "Inter, -apple-system, sans-serif" };

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
@keyframes hf-spin { to { transform: rotate(360deg); } }
@keyframes hf-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
* { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
.hf-scroll { scroll-snap-type: x proximity; -webkit-overflow-scrolling: touch; scroll-padding: 0 10px; }
.hf-scroll > * { scroll-snap-align: start; }
.hf-scroll::-webkit-scrollbar { height: 6px; }
.hf-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
.hf-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
.hf-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); border-radius: 3px; }
.hf-card { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
.hf-card:hover { border-color: rgba(255,255,255,0.1) !important; transform: translateY(-1px); }
.hf-row-hover { transition: background 0.2s; }
.hf-row-hover:hover { background: rgba(255,255,255,0.03) !important; }
.hf-stat-clickable:hover { transform: translateY(-1px); filter: brightness(1.3); }
.hf-cell-hover { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
.hf-cell-hover:hover { filter: brightness(1.4); transform: scale(1.06); z-index: 2; }
.hf-btn-primary { padding: 8px 20px; border-radius: 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.8); font-size: 12px; font-weight: 600; cursor: pointer; font-family: Inter, sans-serif; transition: all 0.2s; letter-spacing: -0.01em; }
.hf-btn-primary:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.12); }
.hf-btn-secondary { padding: 10px 20px; border-radius: 10px; background: rgba(167,139,250,0.06); border: 1px solid rgba(167,139,250,0.12); color: rgba(167,139,250,0.7); font-size: 12px; font-weight: 600; cursor: pointer; font-family: Inter, sans-serif; transition: all 0.2s; }
.hf-btn-secondary:hover { background: rgba(167,139,250,0.1); border-color: rgba(167,139,250,0.2); }
[data-tip] { position: relative; }
[data-tip]:hover::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(22,27,34,0.97);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 10px;
  white-space: nowrap;
  z-index: 10;
  color: rgba(255,255,255,0.6);
  font-weight: 500;
  pointer-events: none;
  font-family: 'JetBrains Mono', monospace;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  backdrop-filter: blur(12px);
}
@media (max-width: 768px) {
  .hf-setup-card { min-width: calc(50% - 12px) !important; max-width: calc(50% - 12px) !important; }
  .hf-closed-sub { display: none !important; }
  .hf-live-time { display: none !important; }
  .hf-wl-range { display: none !important; }
  .hf-wl-name { display: none !important; }
  .hf-banner-prefix { display: none !important; }
  .hf-idx-strip { padding: 8px 16px !important; gap: 14px; justify-content: flex-start !important; scrollbar-width: none; }
  .hf-idx-strip > div { padding-right: 14px !important; }
  .hf-idx-strip::-webkit-scrollbar { display: none; }
  .hf-mob-footer { display: flex !important; }
  .hf-sector-grid { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 6px !important; }
  .hf-sector-chip { flex-direction: column !important; align-items: flex-start !important; padding: 10px 14px !important; gap: 6px !important; }
  .hf-sector-chip span:first-child { width: auto !important; font-size: 11px !important; }
  .hf-sector-chip .hf-sector-bar { width: 100% !important; }
  .hf-sector-chip span:last-child { width: auto !important; }
  .hf-con-row { gap: 6px !important; }
}
@media (max-width: 480px) {
  .hf-main { padding: 20px 16px 48px !important; }
}
`;
